import { ServerStatusNotice } from '@/components/common/ServerStatusNotice';
import React, { useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import { useBranchScope } from '@/hooks/useBranchScope';
import { Loader2 } from 'lucide-react';
import { useT } from '@/contexts/I18nContext';
import { isRetailZone } from '@/lib/zoneComponents';
import {
  getPortfolioReport,
  getPortfolioSummary,
  type PortfolioZoneDto,
  type PortfolioZoneReportDto,
  type TrafficStatus,
} from '@/api/portfolio';
import { rankZones } from './ZoneHealthCard';
import { ZoneDayMiniCard } from './ZoneDayMiniCard';
import { ZoneWeekMiniCard, weekMiniEntries } from './ZoneWeekMiniCard';
import { ShowMoreZonesButton, ZONES_PAGE_SIZE } from './usePagedZones';

// «Все филиалы» report view: mini traffic-light cards for the День/Неделя
// zoom. Pagination is DATA-LEVEL (same as AllBranchesDashboard): the coarse
// summary provides the branch list; the report digest is fetched in pages of
// ≤20 zone_ids so the BE never computes reports for unseen branches.

type DayStatus = TrafficStatus | 'unknown';
const STATUS_RANK: Record<DayStatus, number> = { red: 0, yellow: 1, unknown: 2, green: 3 };

function daySeverity(z: PortfolioZoneReportDto): number {
  let s = 0;
  for (const cat of z.layout ?? []) {
    if (cat.status === 'red') s += 40;
    else if (cat.status === 'yellow') s += 12;
  }
  if (z.queue?.status === 'red') s += 35;
  else if (z.queue?.status === 'yellow') s += 10;
  s += (z.tasks?.overdue ?? 0) * 6;
  s += (z.tables?.episodes ?? 0) * 4;
  return s;
}

function worstDayStatus(z: PortfolioZoneReportDto): DayStatus {
  const statuses: TrafficStatus[] = [
    ...(z.layout ?? []).map(c => c.status),
    ...(z.queue ? [z.queue.status] : []),
  ];
  if ((z.tasks?.overdue ?? 0) > 0 || (z.tables?.episodes ?? 0) > 0) statuses.push('yellow');
  const worst = statuses.reduce<TrafficStatus>((worst, s) => (STATUS_RANK[s] < STATUS_RANK[worst] ? s : worst), 'green');
  const tablesUnavailable = z.tables?.error || z.tables?.no_data || z.tables?.episodes === null;
  return worst === 'green' && tablesUnavailable ? 'unknown' : worst;
}

/** Problems first within a page; quiet cards (closed / no report) sink. */
function rankReportZones(zones: PortfolioZoneReportDto[], zoom: 'day' | 'week'): PortfolioZoneReportDto[] {
  return [...zones].sort((a, b) => {
    const quiet = (z: PortfolioZoneReportDto) => (z.closed || !z.report_exists ? 1 : 0);
    if (quiet(a) !== quiet(b)) return quiet(a) - quiet(b);
    if (zoom === 'week') {
      const lightRank = { red: 0, amber: 1, green: 2 } as const;
      const worst = (z: PortfolioZoneReportDto) => {
        const entries = weekMiniEntries(z.week ?? undefined);
        // Data-starved cards (all-muted) sink like no-data ones — a grey
        // «мало данных» week must not outrank a genuinely healthy branch.
        return entries.length === 0 || entries.every(e => e.muted)
          ? 3
          : Math.min(...entries.map(e => lightRank[e.light]));
      };
      return worst(a) - worst(b);
    }
    return (
      STATUS_RANK[worstDayStatus(a)] - STATUS_RANK[worstDayStatus(b)] ||
      daySeverity(b) - daySeverity(a)
    );
  });
}

const GRID = 'grid gap-4 sm:grid-cols-2 lg:grid-cols-3';

function ReportChunk({
  zones, zoom, date, onOpenZone,
}: {
  zones: PortfolioZoneDto[];
  zoom: 'day' | 'week';
  date: string;
  onOpenZone: (zoneId: string) => void;
}) {
  const { t } = useT();
  const ids = zones.map(z => z.zone_id);
  const { data, error, isValidating } = useSWR(
    ['portfolio-report', zoom, date, ids.join(',')],
    () => getPortfolioReport(zoom, date, ids).then(r => r.data),
    { keepPreviousData: true, revalidateOnFocus: false },
  );

  if (error) {
    return (
      <>
        {zones.map(zone => (
          <ZoneDayMiniCard
            key={zone.zone_id}
            zone={{ zone_id: zone.zone_id, zone_name: zone.zone_name, closed: false,
              report_exists: false, layout: null, queue: null, tables: null, tasks: null, photos: [] }}
            reportError
            onOpen={onOpenZone}
          />
        ))}
      </>
    );
  }

  // Skeletons while the new day/week loads — keepPreviousData otherwise swapped
  // the report silently with NO loader on day change (Alexey 2026-07-21, 793a).
  // isValidating only fires on a key (date/zoom/zones) change here.
  if (!data || isValidating) {
    // Centred, obviously-live loader spanning the grid — a history read can take
    // a few seconds and bare skeletons read as "frozen" (828).
    return (
      <div className="col-span-full flex min-h-40 flex-col items-center justify-center gap-3 py-10 text-muted-foreground">
        <Loader2 className="size-6 animate-spin" aria-hidden />
        <span className="text-sm">{t('portfolio.reportLoading')}</span>
      </div>
    );
  }

  const ranked = rankReportZones(data, zoom);
  return (
    <>
      {ranked.map(zone =>
        zoom === 'week'
          ? <ZoneWeekMiniCard key={zone.zone_id} zone={zone} onOpen={onOpenZone} />
          : <ZoneDayMiniCard key={zone.zone_id} zone={zone} onOpen={onOpenZone} />,
      )}
    </>
  );
}

export function AllBranchesReport({
  zoom, date, onOpenZone,
}: {
  zoom: 'day' | 'week';
  date: string;
  onOpenZone: (zoneId: string) => void;
}) {
  const { t } = useT();
  const [pageCount, setPageCount] = useState(1);

  const { data: summary, isLoading, error } = useSWR(
    'portfolio-summary',
    () => getPortfolioSummary().then(r => r.data),
    { refreshInterval: 60_000, keepPreviousData: true, revalidateOnFocus: false },
  );

  // Frozen order + retail-only — same rationale as AllBranchesDashboard.
  // Группа филиалов в списке филиалов (#1969) — только её филиалы.
  const scope = useBranchScope();
  const orderRef = useRef<string[] | null>(null);
  const ranked = useMemo(() => {
    if (!summary) return undefined;
    const retail = summary.filter(z => isRetailZone(z.zone_type) && (!scope.zoneIds || scope.zoneIds.has(String(z.zone_id))));
    const byId = new Map(retail.map(z => [String(z.zone_id), z]));
    const prev = orderRef.current;
    if (prev && prev.length === byId.size && prev.every(id => byId.has(id))) {
      return prev.map(id => byId.get(id)!);
    }
    const r = rankZones(retail);
    orderRef.current = r.map(z => String(z.zone_id));
    return r;
  }, [summary, scope.zoneIds]);

  // Whole-page «сегодня ещё идёт» when EVERY branch's working day is still
  // logging today (the common daytime case) — instead of a grid of identical
  // labels. Peek at the first page's report (SWR dedupes this with the grid's
  // own chunk-1 fetch, so no extra request); with shared retail hours page 1 is
  // representative. Any mixed / history state falls through to the grid, where
  // each card carries its own «сегодня ещё идёт» / «отчёт формируется» label.
  const firstPageIds = (ranked ?? []).slice(0, ZONES_PAGE_SIZE).map(z => z.zone_id);
  const todayStr = new Date().toLocaleDateString('sv-SE'); // local YYYY-MM-DD
  const isTodayDay = zoom === 'day' && date === todayStr;
  const { data: firstPage } = useSWR(
    ranked && isTodayDay && firstPageIds.length > 0
      ? ['portfolio-report', 'day', date, firstPageIds.join(',')]
      : null,
    () => getPortfolioReport('day', date, firstPageIds).then(r => r.data),
    { keepPreviousData: true, revalidateOnFocus: false },
  );
  const allOngoing =
    isTodayDay &&
    !!firstPage &&
    firstPage.length > 0 &&
    firstPage.every(z => z.today_status === 'ongoing');

  if (error && !ranked) {
    return <ServerStatusNotice error={error} />;
  }

  if (isLoading && !ranked) {
    return (
      <div className={`${GRID} p-4 sm:p-6`}>
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-32 animate-pulse rounded-xl border border-border bg-muted/40" />
        ))}
      </div>
    );
  }

  if (!ranked || ranked.length === 0) {
    return <div className="p-6 text-sm text-muted-foreground">{t('portfolio.noZones')}</div>;
  }
  const chunks: PortfolioZoneDto[][] = [];
  for (let i = 0; i < Math.min(pageCount * ZONES_PAGE_SIZE, ranked.length); i += ZONES_PAGE_SIZE) {
    chunks.push(ranked.slice(i, i + ZONES_PAGE_SIZE));
  }
  const remaining = ranked.length - pageCount * ZONES_PAGE_SIZE;

  if (allOngoing) {
    return (
      <div className="flex min-h-60 flex-col items-center justify-center gap-2 p-6 text-center">
        <span className="text-base font-medium text-foreground">{t('portfolio.day.todayOngoing')}</span>
        <span className="text-sm text-muted-foreground">{t('portfolio.day.todayOngoingHint')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div className={GRID}>
        {chunks.map(chunk => (
          <ReportChunk
            key={chunk.map(z => z.zone_id).join(',')}
            zones={chunk}
            zoom={zoom}
            date={date}
            onOpenZone={onOpenZone}
          />
        ))}
      </div>
      <ShowMoreZonesButton remaining={remaining} onClick={() => setPageCount(c => c + 1)} />
    </div>
  );
}
